import { IconButton, Tooltip } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import {
  Block as BlockIcon,
  Loop as LoopIcon,
  MoreHoriz as MoreHorizIcon,
  Search as PageviewIcon,
  Receipt as ReceiptIcon,
  GridOn as TableChartIcon,
  Timeline as TimelineIcon,
  Description as DescriptionIcon,
  Sync as SyncIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { isEmpty, isFunction } from 'lodash';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import QCXMoveFocusInside from '../../../../../components/focus-lock/QCXMoveFocusInside';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';
import QCXPopupState from '../../../../../components/popup-state/QCXPopupState';
import TarifarioAereoContext from '../../../../../contexts/pricing/tarifario/TarifarioAereoContext';
import { clienteActions } from '../../../../../features/cliente/clienteSlice';
import { IDLE_CONTROL } from '../../../../../features/config-control';
import { setQuery } from '../../../../../features/controller/controllerSlice';
import { setErrorFeedback } from '../../../../../features/feedback/feedbackSlice';
import QCXListItemIcon from '../../../../../shared-components/list-item-icon/QCXListItemIcon';


import {
  selectTarifario,
  selectTarifarios,
  selectTarifariosCount,
  selectHistoryModel,
  selectRefresh,
  selectStatus,
} from '../../../../../features/tarifario/tarifarioSelectors';

import {
  changeToConsultMode,
  changeToCreateMode,
  resetBackgroundMode,
  resetHistoryModel,
  resetMode,
  resetModel,
  resetRefresh,
  resetSubMode,
} from '../../../../../features/tarifario/tarifarioSlice';

import {
  inactivateByIdAsync,
  fetchAgenciamentoWithPaginationAsync,
} from '../../../../../features/tarifario/tarifarioThunks';

import { servicoActions } from '../../../../../features/servico/servicoSlice';
import { transportadoraActions } from '../../../../../features/transportadora/transportadoraSlice';
import SimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import useConfigurePopUp from '../../../../../ts/common/hooks/popUps/useConfigurePopUp';
import { isAlternativeLoadingStatus, isRefresh } from '../../../../../utils/store/store-utils';

export default function TarifarioAereoConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const history = useHistory();

  const { status } = useContext(TarifarioAereoContext);

  const dispatch = useDispatch();

  const refresh = useSelector(selectRefresh);
  const tarifariosList = useSelector(selectTarifarios);
  const historyModel = useSelector(selectHistoryModel);


  const isAlternativeLoading = useMemo(() => isAlternativeLoadingStatus(status), [status]);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
    dispatch(resetBackgroundMode());
    dispatch(resetSubMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
    dispatch(resetHistoryModel());
  }, []);

  useEffect(() => {
    resetModes();
    resetModels();
  }, []);

  const configureDependencies = useCallback(() => {
    dispatch(clienteActions.changeControlTo(IDLE_CONTROL));
    dispatch(servicoActions.changeControlTo(IDLE_CONTROL));
    dispatch(transportadoraActions.changeControlTo(IDLE_CONTROL));
  }, []);


  useEffect(() => {
    const checkIfExistsQuery = () => {
      if (!isEmpty(historyModel) && historyModel?.last?.numero) {
        dispatch(setQuery(historyModel?.last?.numero));
      }
    };

    checkIfExistsQuery();
  }, [historyModel]);


  const [handleActivateOrInactivateByIdClickMiddleware, operationOfActivateOrInactivateById] = useConfigurePopUp({
    primaryConfirmCallback: async (data) => {
      dispatch(inactivateByIdAsync(data));
    },
    initialOptions: {
      title: t('com.muralis.qcx.followup.desativarFollowUp'),
      message: t('com.muralis.qcx.followup.informeMotivoCancelamento'),
    },
  });

  const handleConfigureActivateOrInactivateByIdClick = useCallback(
    (row, popupState) => {
      const { id } = row;

      const handleActivateOrInactivateByIdClick = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState.close(event);

          event.stopPropagation();

          const dataOfOperationActivateOrInactivate = {
            id,
          };

          handleActivateOrInactivateByIdClickMiddleware({ args: dataOfOperationActivateOrInactivate });
        }
      };

      return handleActivateOrInactivateByIdClick;
    },
    [handleActivateOrInactivateByIdClickMiddleware]
  );

  const handleButtonNewClick = useCallback(
    (event) => {
      event.stopPropagation();

      configureDependencies();

      dispatch(changeToCreateMode());

      history.push(t('com.muralis.qcx.url.pricingTarifarioAereoNovo'));
    },
    [history, configureDependencies]
  );

  const handleClickOnEditButton = useCallback(
    (_event, id) => history.push(t('com.muralis.qcx.url.moduloTarifarioAereoDetalhes', { id })),
    [history]
  );


  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: "Id",
        headerAlign: 'center',
        align: 'center',
        flex: 80,
        valueGetter: ({ row }) => row?.id || '-',
      },
      {
        field: 'destino',
        headerName: 'Destino',
        flex: 120,
        valueGetter: ({ row }) => row?.destino?.description || '-',
      },
      {
        field: 'rota',
        headerName: 'Rota',
        flex: 120,
        valueGetter: ({ row }) => row?.vooEstimado || '-',
      },
      {
        field: 'cia-aerea',
        headerName: 'Cia Aérea',
        flex: 60,
        valueGetter: ({ row }) => row?.referencias?.[0] || '-',
      },
      {
        field: '45kg',
        headerName: '45 Kg',
        flex: 60,
        valueGetter: ({ row }) => row?.tarifa45kg || '-',
      },
      {
        field: '100kg',
        headerName: '100 Kg',
        flex: 60,
        valueGetter: ({ row }) => row?.tarifa100kg || '-',
      },
      {
        field: '300kg',
        headerName: '300 Kg',
        flex: 60,
        valueGetter: ({ row }) => row?.tarifa300kg || '-',
      },
      {
        field: '500kg',
        headerName: '500 Kg',
        flex: 60,
        valueGetter: ({ row }) => row?.tarifa500kg || '-',
      },
      {
        field: '1000kg',
        headerName: '1000 Kg',
        flex: 60,
        valueGetter: ({ row }) => row?.tarifa1000kg || '-',
      },
      {
        field: 'tt',
        headerName: 'TT',
        flex: 60,
        valueGetter: ({ row }) => row?.transitTime || '-',
      },
      {
        field: 'voo-estimado',
        headerName: 'Vôo Estimado',
        flex: 60,
        valueGetter: ({ row }) => row?.vooEstimado || '-',
      },
      {
        field: 'obs',
        headerName: 'Observação',
        flex: 120,
        valueGetter: ({ row }) => row?.observacao?.length > 32 ? `${row.observacao.substring(0, 32)}...` : row?.observacao || '-',
      },
      {
        field: 'acoes',
        headerName: t('com.muralis.qcx.acoes.label'),
        width: 150,
        align: 'center',
        renderCell: ({ row: { id, active } }) => (
          <QCXPopupState popupId="popup-menu-catalogo-produtos">
            {(popupState) => (
              <>
                <IconButton key={`btn-more-options-${id}`} name={`btn-more-options-${id}`} {...bindTrigger(popupState)}>
                  <MoreHorizIcon color="secondary" size={20} />
                </IconButton>
                <Menu {...bindMenu(popupState)}>
                  <MenuItem
                    onClick={(event) => {
                      popupState.close(event);
  
                      handleClickOnEditButton(event, id);
                    }}
                  >
                    <QCXListItemIcon>
                      <PageviewIcon color="primary" />
                    </QCXListItemIcon>
                    <Typography
                      variant="inherit"
                      style={{
                        fontSize: 12,
                      }}
                    >
                      {t('com.muralis.qcx.acoes.consultar')}
                    </Typography>
                  </MenuItem>

                  {userHasDeleteRole(authInfo, 'catalogo-produtos') ? (
                    <MenuItem
                      onClick={(event) => {
                        popupState.close(event);
  
                        // handleActivateInactivate(event, id);
                      }}
                    >
                      <QCXListItemIcon>
                        <DeleteIcon color="error" />
                      </QCXListItemIcon>
                      <Typography
                        variant="inherit"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        {t('com.muralis.qcx.acoes.excluir')}
                      </Typography>
                    </MenuItem>
                  ) : null}
                </Menu>
              </>
            )}
          </QCXPopupState>
        ),
      },
    ],
    [
      handleConfigureActivateOrInactivateByIdClick,
    ]
  );

  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloPricing'),
        name: t('com.muralis.qcx.pricingModule.pricingLabel'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.pricingModule.tarifarioAereoLabel'),
      },
      default: true,
    },
  ];

  const handleConfigChange = useCallback(({ currentPage, pageSize, filterInputValue, advancedFilter }) => {
    const referencia = advancedFilter?.referencia ? advancedFilter?.referencia : undefined;

    dispatch(
      fetchAgenciamentoWithPaginationAsync([
        { name: 'page', value: currentPage },
        { name: 'size', value: pageSize },
        { name: 'processo', value: filterInputValue },
        { name: 'referencia', value: referencia },
        { name: 'via-transporte', value: "aerea" },
      ])
    );
  }, []);

  const rowsCount = useSelector(selectTarifariosCount);

  const templateProps = {
    page: {
      title: t('com.muralis.qcx.pricingModule.tarifarioAereoLabel'),
      icon: <ReceiptIcon />,
      breadcrumbs,
    },
    control: {
      buttonNew: {
        description: t('com.muralis.qcx.pricingModule.novoTarifario'),
        onClick: handleButtonNewClick,
      },
    },
    table: {
      columns,
      rowsCount,
      onConfigChangeCallback: handleConfigChange,
    },
  };

  return (
    <SimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={tarifariosList}
      isLoading={isAlternativeLoading}
      authInfo={authInfo}
      requiredRoles={['importacao-follow-up']}
      advancedFilterKeyAccessors={['referencia']}
    >
      <></>
    </SimpleConsultPageTemplate>
  );
}
